// lib
import moment from 'moment'
import { List } from 'immutable'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import React, { useEffect, useState } from 'react'
import { preprocessText, escapeText } from '@aytm/lib/common/func'

// components
import LightBox from '@aytm/lib/rfd-ui/components/basic/light-box'

// styles
import '@aytm/lib/rfd-ui/styles/basic/video-popup.scss'

// constants
const FRAME_PADDING = 7
const LABEL_HEIGHT = 40

const VIDEO_WIDTH = 530
const VIDEO_RATIO = 520 / 270

// <Component>
VideoPopup.propTypes = {
  videos: PropTypes.instanceOf(List).isRequired,
  videoId: PropTypes.string,
  videoStartTime: PropTypes.number, // seconds
  show: PropTypes.bool,
  onClose: PropTypes.func
}

export default function VideoPopup(props) {
  const { show, videos, videoId, videoStartTime, onClose } = props

  const [currentVideoId, setCurrentVideoId] = useState('')
  const [startTime, setStartTime] = useState(0)

  const setVideo = () => {
    const { videos, videoId, videoStartTime } = props
    const currentVideoId = videoId || videos.getIn([0, 'id'])
    setCurrentVideoId(List.isList(currentVideoId) ? currentVideoId.get(0) : currentVideoId)
    setStartTime(videoStartTime || 0)
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => void setVideo(), [])

  useEffect(() => {
    if (videoId) setCurrentVideoId(videoId)
    setStartTime(videoStartTime || 0)
  }, [videoId, videoStartTime])

  const onCloseClick = event => {
    if ('function' === typeof onClose) {
      onClose()
    }
    try {
      event?.preventDefault?.()
      event?.stopPropagation?.()
      event?.stopImmediatePropagation?.()
    } catch {
      //
    }
  }

  const onPrevClick = event => {
    event.nativeEvent.stopImmediatePropagation()
    const currentVideoIndex = videos.findIndex(v =>
      List.isList(v.get('id')) ? v.get('id').some(id => id == currentVideoId) : v.get('id') == currentVideoId
    )
    const prevVideoIndex = Math.max(currentVideoIndex - 1, 0)
    const prevVideoId = videos.getIn([prevVideoIndex, 'id'])
    setCurrentVideoId(List.isList(prevVideoId) ? prevVideoId.get(0) : prevVideoId)
    setStartTime(0)
  }

  const onNextClick = event => {
    event.nativeEvent.stopImmediatePropagation()
    const currentVideoIndex = videos.findIndex(v =>
      List.isList(v.get('id')) ? v.get('id').some(id => id == currentVideoId) : v.get('id') == currentVideoId
    )
    const nextVideoIndex = Math.min(currentVideoIndex + 1, videos.size - 1)
    const nextVideoId = videos.getIn([nextVideoIndex, 'id'])
    setCurrentVideoId(List.isList(nextVideoId) ? nextVideoId.get(0) : nextVideoId)
    setStartTime(0)
  }

  const currentVideoIndex = videos.findIndex(v =>
    List.isList(v.get('id')) ? v.get('id').some(id => id == currentVideoId) : v.get('id') == currentVideoId
  )
  const currentVideo = videos.get(currentVideoIndex)
  const rel = currentVideo.get('rel')
  const autoplay = currentVideo.get('autoplay')

  // vimeo start time url param
  const duration = moment.duration(startTime, 'seconds')
  const vimeoStartTime = 't={mm}m{ss}s'.replace('{mm}', duration.minutes()).replace('{ss}', duration.seconds())

  const label = currentVideo.get('title')
  const params = []
  if (autoplay) params.push('autoplay=1')
  if (startTime) params.push(`start=${startTime}#${vimeoStartTime}`) // should be pushed as last param
  const src = `${currentVideo.get('url')}${currentVideo.get('code')}${
    (rel ? rel + '&amp;' : '?') + params.join('&amp;')
  }`
  const iframe = `<iframe width="${VIDEO_WIDTH}" height="${
    VIDEO_WIDTH / VIDEO_RATIO
  }" src="${src}" frameborder="0"  allow="${
    autoplay ? 'autoplay; ' : ''
  }fullscreen" allowfullscreen="true" allowscriptaccess="always"/>`

  return show ? (
    <LightBox onClose={onCloseClick}>
      <div
        className="rfd-ui-video-popup-frame"
        style={{
          width: `${VIDEO_WIDTH}px`,
          height: `${VIDEO_WIDTH / VIDEO_RATIO + (label || 1 < videos.size ? LABEL_HEIGHT : 0)}px`,
          margin: `-${
            VIDEO_WIDTH / (2 * VIDEO_RATIO) + FRAME_PADDING + (label || 1 < videos.size ? LABEL_HEIGHT / 2 : 0)
          }px 0 0 -${VIDEO_WIDTH / 2 + FRAME_PADDING}px`
        }}
      >
        <div className="rfd-ui-video-popup-close-button" onMouseDown={onCloseClick}>
          <i className="icon-aytmfd-delete" />
        </div>
        <div className="rfd-ui-video-popup-video" dangerouslySetInnerHTML={{ __html: iframe }} />
        {(label || videos.size > 1) && (
          <div className="rfd-ui-video-popup-footer" style={{ height: `${LABEL_HEIGHT}px` }}>
            {label && (
              <div
                className="rfd-ui-video-popup-video-label"
                dangerouslySetInnerHTML={{ __html: preprocessText(escapeText(label)) }}
              />
            )}
            {videos.size > 1 && (
              <div className="rfd-ui-video-popup-video-controls">
                <i
                  className={classNames('icon-aytmfd-prev', {
                    disabled: 0 == currentVideoIndex
                  })}
                  onMouseDown={onPrevClick}
                />
                <i
                  className={classNames('icon-aytmfd-next', {
                    disabled: currentVideoIndex == videos.size - 1
                  })}
                  onMouseDown={onNextClick}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </LightBox>
  ) : null
}
